import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import {
  Eye, CheckCircle, X, Ticket, Star,
  Phone, Mail, User, AlignLeft, UserCheck,
  Clock, ArrowRightLeft, ChevronDown,
} from "lucide-react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import Sidebar from "../components/Sidebar";
import SearchableDropdown from "../components/SearchableDropdown";
import TableHeaderFilter from "../components/TableHeaderfilter";
import DateSearch from "../components/DateSearch";
import { SERVER_URL } from "../config";

/* ═══════════════════════════════════════════════════════════
   SHARED STYLES
═══════════════════════════════════════════════════════════ */
const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100";

/* ═══════════════════════════════════════════════════════════
   PURE HELPERS
═══════════════════════════════════════════════════════════ */

/** Format a JS Date / ISO string → "DD/MM/YYYY, HH:MM AM/PM" in IST */
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      })
    : "—";

/**
 * Human-readable duration between two ISO/Date values.
 * e.g. "2h 15m", "45m", "3d 4h"
 */
const fmtDuration = (from, to) => {
  if (!from || !to) return "—";
  const ms = new Date(to) - new Date(from);
  if (ms < 0) return "—";
  const mins  = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days  > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
};

/* ═══════════════════════════════════════════════════════════
   SMALL UI ATOMS
═══════════════════════════════════════════════════════════ */
const Badge = ({ status }) => {
  const map = {
    open:        "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    resolved:    "bg-teal-100 text-teal-700",
    closed:      "bg-slate-200 text-slate-600",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize whitespace-nowrap ${map[status] ?? "bg-gray-100 text-gray-500"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

const PriorityBadge = ({ value }) => {
  const map = {
    low:      "bg-slate-100 text-slate-500",
    medium:   "bg-blue-100 text-blue-600",
    high:     "bg-orange-100 text-orange-600",
    critical: "bg-red-100 text-red-700",
  };
  return value
    ? <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[value] ?? "bg-gray-100 text-gray-500"}`}>{value}</span>
    : <span className="text-slate-400 text-xs">—</span>;
};

const StarRating = ({ value }) => {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span className="flex justify-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={13}
          className={i <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} />
      ))}
    </span>
  );
};

const StatCard = ({ title, count, color }) => {
  const themes = {
    indigo:  { dot: "bg-indigo-500", accent: "text-indigo-700",  bar: "from-indigo-500 to-indigo-600"  },
    emerald: { dot: "bg-emerald-500",accent: "text-emerald-700", bar: "from-emerald-500 to-emerald-600"},
    amber:   { dot: "bg-amber-500",  accent: "text-amber-700",   bar: "from-amber-500 to-amber-600"   },
    rose:    { dot: "bg-rose-500",   accent: "text-rose-700",    bar: "from-rose-500 to-rose-600"     },
  };
  const t = themes[color] || themes.indigo;
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex flex-col gap-2"
      style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
      <div className="flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
        <p className={`text-[10px] font-bold uppercase tracking-widest ${t.accent}`}>{title}</p>
      </div>
      <p className="text-3xl font-extrabold text-slate-800">{count}</p>
      <div className="w-full h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${t.bar}`} style={{ width: "60%" }} />
      </div>
    </div>
  );
};

/* Scrollable modal */
const Modal = ({ children, onClose, wide, extraWide }) => (
  <div className="fixed inset-0 bg-black/50 flex justify-center items-start pt-8 pb-8 z-50 overflow-y-auto">
    <div className={`bg-white rounded-2xl shadow-2xl w-full mx-4 relative
      ${extraWide ? "max-w-4xl" : wide ? "max-w-2xl" : "max-w-lg"}`}>
      <button
        className="absolute right-4 top-4 text-gray-400 hover:text-gray-700 z-10"
        onClick={onClose}
      >
        <X size={20} />
      </button>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

/* Inline suggestion list */
const SuggestionList = ({ suggestions, onSelect }) => {
  if (!suggestions.length) return null;
  return (
    <div className="absolute z-50 top-full left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-44 overflow-y-auto">
      {suggestions.map((s, i) => (
        <div key={i}
          className="px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50 cursor-pointer flex items-center gap-2"
          onMouseDown={() => onSelect(s)}>
          <User size={12} className="text-slate-400 shrink-0" />
          <span>{s.contact_person}</span>
          {s.contact_no && <span className="text-slate-400 text-xs ml-auto">{s.contact_no}</span>}
        </div>
      ))}
    </div>
  );
};

/* Duration timeline chip */
const DurationChip = ({ label, duration, isActive }) => (
  <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
    ${isActive ? "bg-amber-50 border border-amber-200 text-amber-700" : "bg-slate-50 border border-slate-200 text-slate-600"}`}>
    <Clock size={11} className={isActive ? "text-amber-500" : "text-slate-400"} />
    <span className="font-semibold">{label}:</span>
    <span>{duration}</span>
    {isActive && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function TicketingPage() {
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [tickets, setTickets]           = useState([]);
  const [employees, setEmployees]       = useState([]);
  const [activeTab, setActiveTab]       = useState("all");
  const [dateFilter, setDateFilter]     = useState({ start: "", end: "" });
  const [now, setNow]                   = useState(new Date()); // live clock for active durations

  /* ── Modals ── */
  const [showForm,      setShowForm]       = useState(false);
  const [closeModal,    setCloseModal]     = useState(null);
  const [viewTicket,    setViewTicket]     = useState(null);
  const [reassignModal, setReassignModal]  = useState(null); // ticket object

  /* ── Column filters ── */
  const [filters, setFilters] = useState({
    ticketId: [], tallySlNo: [], customerName: [], createdBy: [], service: [], assignTo: [],
  });

  /* ── Auto-fetch / lookup ── */
  const [lookupLoading,      setLookupLoading]      = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showSuggestions,    setShowSuggestions]    = useState(false);
  const contactRef = useRef(null);

  /* ── Form ── */
  const emptyForm = {
    ProductType: "", TallySlNo: "", customerName: "", contact_no: "", emailId: "",
    contact_person: "", assigned_to: "", serviceType: "", description: "", priority: "medium",
    _autoFilled: false, _allFieldsEditable: false, _contacts: [],
  };
  const [form,         setForm]         = useState(emptyForm);
  const [closeRemarks, setCloseRemarks] = useState("");
  const [submitting,   setSubmitting]   = useState(false);
  const [reassignTo,   setReassignTo]   = useState("");

  /* ── Logged-in employee ── */
  const loggedIn = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("employee") || "{}"); }
    catch { return {}; }
  }, []);
  const employeeId   = loggedIn.employee_id   ?? "";
  const employeeName = loggedIn.employee_name ?? "";

  /* ── Live clock – tick every minute ── */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  /* ══════════════════════════════════════════
     DATA FETCHING
  ══════════════════════════════════════════ */
  useEffect(() => { fetchAll(); }, [activeTab]);

  const fetchAll = useCallback(async () => {
    const tabParam = activeTab === "open" ? "open" : activeTab === "closed" ? "closed" : "all";
    try {
      const [tRes, eRes] = await Promise.all([
        axios.get(`${SERVER_URL}/Rahul/get_tickets.php?employee_id=${employeeId}&status=${tabParam}`),
        axios.get(`${SERVER_URL}/Rahul/get_employee.php`),
      ]);
      setTickets(tRes.data.data ?? []);
      setEmployees(eRes.data ?? []);
    } catch (e) { console.error(e); }
  }, [activeTab, employeeId]);

  /* ══════════════════════════════════════════
     LOOKUP  (TallySlNo or Email)
     Called on Enter-key or blur of either field
  ══════════════════════════════════════════ */
  const lookupCustomer = useCallback(async (slNo, email) => {
    const sl = (slNo || "").trim();
    const em = (email || "").trim();
    if (!sl && !em) return;
    setLookupLoading(true);
    try {
      // TODO: replace with your actual endpoint
      // Expected: { status: true, data: { customerName, emailId, contact_person, contact_no,
      //                                    contacts: [{contact_person, contact_no}] } }
      const res = await axios.get(`${SERVER_URL}/Rahul/lookupCustomer.php`, {
        params: { tallySlNo: sl, emailId: em },
      });
      if (res.data.status && res.data.data) {
        const d        = res.data.data;
        const contacts = d.contacts || [];
        const latest   = contacts[0] ?? { contact_person: d.contact_person, contact_no: d.contact_no };
        setForm(prev => ({
          ...prev,
          customerName:       d.customerName    || prev.customerName,
          emailId:            d.emailId         || prev.emailId,
          contact_person:     latest.contact_person || "",
          contact_no:         latest.contact_no     || "",
          _autoFilled:        true,
          _allFieldsEditable: false,
          _contacts:          contacts,
        }));
        if (contacts.length > 1) { setContactSuggestions(contacts); setShowSuggestions(true); }
        else                     { setContactSuggestions([]); setShowSuggestions(false); }
      } else {
        // No match – let user fill everything manually
        setForm(prev => ({ ...prev, _autoFilled: false, _allFieldsEditable: true, _contacts: [] }));
        setContactSuggestions([]); setShowSuggestions(false);
      }
    } catch (e) {
      console.error(e);
      setForm(prev => ({ ...prev, _allFieldsEditable: true }));
    }
    setLookupLoading(false);
  }, []);

  const handleSlNoKeyDown  = (e) => { if (e.key === "Enter") lookupCustomer(form.TallySlNo, ""); };
  const handleEmailKeyDown = (e) => { if (e.key === "Enter" && !form.TallySlNo) lookupCustomer("", form.emailId); };

  const isReadOnly = (field) => {
    if (!form._autoFilled || form._allFieldsEditable) return false;
    return ["customerName", "emailId"].includes(field);
  };

  /* ── Contact-person suggestions while typing ── */
  const handleContactPersonChange = (e) => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, contact_person: val }));
    const contacts = form._contacts || [];
    if (val && contacts.length > 0) {
      const filtered = contacts.filter(c =>
        c.contact_person?.toLowerCase().includes(val.toLowerCase())
      );
      setContactSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (s) => {
    setForm(prev => ({ ...prev, contact_person: s.contact_person, contact_no: s.contact_no }));
    setShowSuggestions(false);
  };

  /* ══════════════════════════════════════════
     DERIVED DATA
  ══════════════════════════════════════════ */
  const dateFilteredTickets = useMemo(() => {
    if (!dateFilter.start || !dateFilter.end) return tickets;
    return tickets.filter(t => {
      if (!t.generated_date) return false;
      const d = String(t.generated_date).split(" ")[0];
      return d >= dateFilter.start && d <= dateFilter.end;
    });
  }, [tickets, dateFilter]);

  const filteredTickets = useMemo(() => {
    return dateFilteredTickets.filter(t =>
      (!filters.ticketId.length     || filters.ticketId.includes(t.ticket_id)) &&
      (!filters.tallySlNo.length    || filters.tallySlNo.includes(t.TallySlNo)) &&
      (!filters.customerName.length || filters.customerName.includes(t.customerName)) &&
      (!filters.createdBy.length    || filters.createdBy.includes(t.created_by)) &&
      (!filters.service.length      || filters.service.includes(t.serviceType)) &&
      (!filters.assignTo.length     || filters.assignTo.includes(t.assigned_to))
    );
  }, [dateFilteredTickets, filters]);

  const ticketStats = useMemo(() => {
    const all        = dateFilteredTickets.length;
    const open       = dateFilteredTickets.filter(t => t.status === "open").length;
    const inProgress = dateFilteredTickets.filter(t => t.status === "in_progress").length;
    const closed     = dateFilteredTickets.filter(t => t.status === "closed").length;
    return { all, open, inProgress, closed };
  }, [dateFilteredTickets]);

  const employeeOptions = employees.map(e => ({ label: `${e.name} (${e.id})`, value: e.id }));
  const productOptions  = [
    { label: "Tally Prime",    value: "Tally Prime"    },
    { label: "Cloud",          value: "Cloud"          },
    { label: "Customization",  value: "Customization"  },
  ];
  const supportOptions  = [
    { label: "Installation", value: "Installation" },
    { label: "Complaint",    value: "Complaint"    },
    { label: "Maintenance",  value: "Maintenance"  },
    { label: "Other",        value: "Other"        },
  ];
  const priorityOptions = [
    { label: "Low",      value: "low"      },
    { label: "Medium",   value: "medium"   },
    { label: "High",     value: "high"     },
    { label: "Critical", value: "critical" },
  ];

  /* ══════════════════════════════════════════
     DURATION LOGIC
     Each ticket should have an `assignment_log` array from the API:
     [{ employee_id, employee_name, assigned_at, released_at | null }]
     If not present we fall back to the flat fields.
  ══════════════════════════════════════════ */

  /**
   * Returns an array of { name, from, to, isActive } segments.
   * `to` is null for the last (still-active) segment.
   */
  const buildSegments = (ticket) => {
    const log = ticket.assignment_log;

    // Rich log from backend (preferred)
    if (Array.isArray(log) && log.length > 0) {
      return log.map((entry, i) => ({
        name:     entry.employee_name || entry.employee_id,
        from:     entry.assigned_at,
        to:       entry.released_at || null,
        isActive: !entry.released_at && ticket.status !== "closed",
      }));
    }

    // Fallback: single entry from flat ticket fields
    return [{
      name:     ticket.assigned_person || ticket.created_by_name || "—",
      from:     ticket.generated_date,
      to:       ticket.status === "closed" ? ticket.closed_date : null,
      isActive: ticket.status !== "closed",
    }];
  };

  /** Compute readable duration for a segment */
  const segmentDuration = (seg) => {
    const end = seg.to || now.toISOString();
    return fmtDuration(seg.from, end);
  };

  /* ══════════════════════════════════════════
     ACTIONS
  ══════════════════════════════════════════ */
  const handleSubmit = async () => {
    if (!form.customerName || !form.contact_no) {
      alert("Customer Name and Contact No. are required.");
      return;
    }
    setSubmitting(true);
    try {
      // TODO: attach your endpoint
      const res = await axios.post(`${SERVER_URL}/Rahul/generate_ticket.php`, {
        ...form, created_by: employeeId,
      });
      if (res.data.success) {
        alert(`✅ Ticket Generated!\nTicket ID: ${res.data.ticket_id}\nSMS Sent: ${res.data.sms_sent ? "Yes" : "No"}`);
        setShowForm(false); setForm(emptyForm); fetchAll();
      } else { alert("Error: " + res.data.message); }
    } catch (e) { alert("Request failed: " + e.message); }
    setSubmitting(false);
  };

  const handleClose = async () => {
    if (!closeModal) return;
    setSubmitting(true);
    try {
      // TODO: attach your endpoint
      const res = await axios.post(`${SERVER_URL}/Rahul/update_ticket.php`, {
        ticket_id: closeModal.ticket_id,
        remarks:   closeRemarks,
      });
      if (res.data.success) {
        alert(`✅ Ticket closed.\nEmail sent: ${res.data.email_sent ? "Yes" : "No"}`);
        setCloseModal(null); setCloseRemarks(""); fetchAll();
      } else { alert("Error: " + res.data.message); }
    } catch (e) { alert("Request failed: " + e.message); }
    setSubmitting(false);
  };

  const handleReassign = async () => {
    if (!reassignTo || !reassignModal) return;
    setSubmitting(true);
    try {
      // TODO: attach your endpoint
      // Expect backend to log old assignment end-time and new start-time
      const res = await axios.post(`${SERVER_URL}/Rahul/assign_ticket.php`, {
        ticket_id:   reassignModal.ticket_id,
        assigned_to: reassignTo,
        reassigned_by: employeeId,
      });
      if (res.data.success) {
        alert("✅ Ticket reassigned successfully.");
        setReassignModal(null); setReassignTo(""); fetchAll();
      } else { alert("Error: " + res.data.message); }
    } catch (e) { alert("Request failed: " + e.message); }
    setSubmitting(false);
  };

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="flex h-screen overflow-hidden" style={{ background: "#f0f4f8", fontFamily: "'Inter', sans-serif" }}>

        {/* top accent */}
        <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
          style={{ background: "linear-gradient(90deg,#4f46e5,#6366f1,#818cf8)" }} />

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative z-10">

          {/* ── Page header ── */}
          <header className="flex-shrink-0 sticky top-0 z-30 border-b border-slate-200"
            style={{ background: "#fff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
            <div className="px-7 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                  onClick={() => setSidebarOpen(true)}>
                  <Bars3Icon className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2.5">
                  <div className="w-1 h-6 rounded-full"
                    style={{ background: "linear-gradient(180deg,#4f46e5,#6366f1)" }} />
                  <h1 className="text-lg font-bold tracking-tight text-slate-800 flex items-center gap-2">
                    <Ticket size={18} className="text-indigo-600" /> Ticketing Management
                  </h1>
                </div>
              </div>
              <button
                onClick={() => { setForm(emptyForm); setShowForm(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 8px rgba(79,70,229,0.3)" }}>
                <Ticket size={14} /> Generate Ticket
              </button>
            </div>
          </header>

          {/* ── Scrollable content ── */}
          <main className="flex-1 overflow-y-auto att-scrollbar min-h-0">
            <div className="p-5 max-w-[1700px] mx-auto flex flex-col gap-5">

              {/* Stats */}
              {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatCard title="Total" count={ticketStats.all}        color="indigo"  />
                <StatCard title="Open"  count={ticketStats.open}       color="amber"   />
                <StatCard title="In Progress" count={ticketStats.inProgress} color="rose" />
                <StatCard title="Closed" count={ticketStats.closed}    color="emerald" />
              </div> */}

              {/* Filter bar */}
              <div className="px-5 py-3.5 rounded-xl border border-slate-200 bg-white"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <DateSearch onSearch={(s, e, isF) =>
                    isF ? setDateFilter({ start: s, end: e })
                        : setDateFilter({ start: "", end: "" })} />
                  <div className="flex gap-2 flex-wrap">
                    {["all", "open", "closed"].map(tab => (
                      <button key={tab} onClick={() => setActiveTab(tab)}
                        className={`px-5 py-1.5 rounded-full text-sm font-medium transition capitalize
                          ${activeTab === tab
                            ? "text-white shadow"
                            : "bg-white border text-gray-600 hover:bg-indigo-50"}`}
                        style={activeTab === tab ? { background: "linear-gradient(135deg,#4f46e5,#6366f1)" } : {}}>
                        {tab}
                      </button>
                    ))}
                    <span className="text-xs text-gray-400 self-center ml-1">{filteredTickets.length} record(s)</span>
                  </div>
                </div>
              </div>

              {/* ── TABLE CONTAINER ──
                  The outer div is fixed-height and clips overflow.
                  thead is sticky inside the scroll container so ONLY tbody scrolls.
              ── */}
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)", minHeight: 0, maxHeight: "calc(100vh - 340px)" }}>
                <div className="overflow-auto att-scrollbar flex-1 min-h-0">
                  <table className="w-full text-sm border-collapse" style={{ minWidth: "1400px" }}>

                    {/* ── STICKY THEAD ── */}
                    <thead style={{ position: "sticky", top: 0, zIndex: 10, background: "#f8fafc" }}>
                      <tr>
                        {/* Ticket ID */}
                        <th className="py-3 px-3 text-left border-b border-slate-200 whitespace-nowrap">
                          <TableHeaderFilter label="Ticket ID"
                            options={[...new Set(tickets.map(t => t.ticket_id))].map(v => ({ label: v, value: v }))}
                            selectedValues={filters.ticketId}
                            onChange={v => setFilters(f => ({ ...f, ticketId: v }))} />
                        </th>
                        {/* Tally SL No */}
                        <th className="py-3 px-3 text-left border-b border-slate-200 whitespace-nowrap">
                          <TableHeaderFilter label="Tally SL No."
                            options={[...new Set(tickets.map(t => t.TallySlNo).filter(Boolean))].map(v => ({ label: v, value: v }))}
                            selectedValues={filters.tallySlNo}
                            onChange={v => setFilters(f => ({ ...f, tallySlNo: v }))} />
                        </th>
                        {/* Generated Date */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-left">
                          Generated Date
                        </th>
                        {/* Customer */}
                        <th className="py-3 px-3 text-left border-b border-slate-200">
                          <TableHeaderFilter label="Customer"
                            options={[...new Set(tickets.map(t => t.customerName))].map(v => ({ label: v, value: v }))}
                            selectedValues={filters.customerName}
                            onChange={v => setFilters(f => ({ ...f, customerName: v }))} />
                        </th>
                        {/* Created By */}
                        <th className="py-3 px-3 text-left border-b border-slate-200">
                          <TableHeaderFilter label="Created By"
                            options={[...new Set(tickets.map(t => t.created_by_name))].filter(Boolean).map(name => {
                              const tk = tickets.find(t => t.created_by_name === name);
                              return { label: name, value: tk?.created_by ?? name };
                            })}
                            selectedValues={filters.createdBy}
                            onChange={v => setFilters(f => ({ ...f, createdBy: v }))} />
                        </th>
                        {/* Service */}
                        <th className="py-3 px-3 text-left border-b border-slate-200">
                          <TableHeaderFilter label="Service"
                            options={[...new Set(tickets.map(t => t.serviceType).filter(Boolean))].map(v => ({ label: v, value: v }))}
                            selectedValues={filters.service}
                            onChange={v => setFilters(f => ({ ...f, service: v }))} />
                        </th>
                        {/* Assigned To */}
                        <th className="py-3 px-3 text-left border-b border-slate-200">
                          <TableHeaderFilter label="Assigned To"
                            options={[...new Set(tickets.map(t => t.assigned_person).filter(Boolean))].map(name => {
                              const tk = tickets.find(t => t.assigned_person === name);
                              return { label: name, value: tk?.assigned_to ?? name };
                            })}
                            selectedValues={filters.assignTo}
                            onChange={v => setFilters(f => ({ ...f, assignTo: v }))} />
                        </th>
                        {/* Priority */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-left">
                          Priority
                        </th>
                        {/* Status */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                          Status
                        </th>
                        {/* Duration summary */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-left">
                          Duration
                        </th>
                        {/* Closed Date */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-left">
                          Closed Date
                        </th>
                        {/* Feedback */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">
                          Feedback
                        </th>
                        {/* Actions */}
                        <th className="py-3 px-3 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    {/* ── TBODY (scrolls) ── */}
                    <tbody>
                      {filteredTickets.length === 0 && (
                        <tr>
                          <td colSpan={13} className="text-center py-16 text-slate-400 text-sm">
                            No tickets found.
                          </td>
                        </tr>
                      )}

                      {filteredTickets.map(t => {
                        const segments = buildSegments(t);
                        const totalDur = fmtDuration(
                          t.generated_date,
                          t.status === "closed" ? t.closed_date : now.toISOString()
                        );

                        return (
                          <tr key={t.ticket_id}
                            className="border-t border-slate-100 hover:bg-indigo-50/40 transition-colors">
                            <td className="py-3 px-3 font-mono text-xs text-indigo-700 whitespace-nowrap">{t.ticket_id}</td>
                            <td className="py-3 px-3 text-xs text-slate-600 whitespace-nowrap">{t.TallySlNo || "—"}</td>
                            <td className="py-3 px-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(t.generated_date)}</td>
                            <td className="py-3 px-3 text-xs text-slate-700 max-w-[180px] truncate">{t.customerName}</td>
                            <td className="py-3 px-3 text-xs text-slate-600 whitespace-nowrap">{t.created_by_name}</td>
                            <td className="py-3 px-3 text-xs text-slate-600">{t.serviceType || "—"}</td>
                            <td className="py-3 px-3 text-xs text-slate-600 whitespace-nowrap">{t.assigned_person ?? "—"}</td>
                            <td className="py-3 px-3"><PriorityBadge value={t.priority} /></td>
                            <td className="py-3 px-3"><Badge status={t.status} /></td>

                            {/* Duration chip */}
                            <td className="py-3 px-3 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg
                                ${t.status !== "closed"
                                  ? "bg-amber-50 border border-amber-200 text-amber-700"
                                  : "bg-slate-50 border border-slate-200 text-slate-500"}`}>
                                <Clock size={11} />
                                {totalDur}
                                {t.status !== "closed" &&
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse ml-0.5" />}
                              </span>
                            </td>

                            <td className="py-3 px-3 text-xs text-slate-500 whitespace-nowrap">
                              {t.status === "closed" && t.closed_date ? fmtDate(t.closed_date) : "—"}
                            </td>
                            <td className="py-3 px-3 text-center"><StarRating value={t.feedback} /></td>

                            {/* Action buttons */}
                            <td className="py-3 px-3">
                              <div className="flex justify-center items-center gap-1.5">
                                {/* View */}
                                <button title="View Details" onClick={() => setViewTicket(t)}
                                  className="p-1.5 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 transition-colors">
                                  <Eye size={13} />
                                </button>
                                {/* Reassign (only if not closed) */}
                                {t.status !== "closed" && (
                                  <button title="Reassign" onClick={() => { setReassignModal(t); setReassignTo(""); }}
                                    className="p-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors">
                                    <ArrowRightLeft size={13} />
                                  </button>
                                )}
                                {/* Close */}
                                {t.status !== "closed" && (
                                  <button title="Close Ticket" onClick={() => { setCloseModal(t); setCloseRemarks(""); }}
                                    className="p-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors">
                                    <CheckCircle size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* end table container */}

            </div>
          </main>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          GENERATE TICKET MODAL
      ══════════════════════════════════════════════ */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)} wide>
          <h2 className="text-lg font-bold text-indigo-900 mb-5 flex items-center gap-2">
            <Ticket size={18} className="text-indigo-600" /> Generate New Ticket
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Product Type */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Product Type *</label>
              <SearchableDropdown options={productOptions} value={form.ProductType}
                onChange={v => setForm({ ...form, ProductType: v })} placeholder="Select Product Type" />
            </div>

            {/* Tally SL No — lookup on Enter or blur */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">
                Tally SL No.
                {lookupLoading && <span className="ml-2 text-indigo-500 font-normal normal-case">Fetching…</span>}
              </label>
              <input className={inputStyle}
                placeholder="Enter & press Enter to auto-fill"
                value={form.TallySlNo}
                onChange={e => setForm({ ...form, TallySlNo: e.target.value, _autoFilled: false })}
                onKeyDown={handleSlNoKeyDown}
                onBlur={() => lookupCustomer(form.TallySlNo, "")}
              />
            </div>

            {/* Customer Name */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Customer Name *</label>
              <input className={inputStyle} placeholder="Customer Name"
                value={form.customerName}
                onChange={e => setForm({ ...form, customerName: e.target.value })}
                readOnly={isReadOnly("customerName")} />
            </div>

            {/* Email — lookup on Enter or blur (only when no SL No) */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Email ID</label>
              <div className="relative">
                <Mail size={13} className="absolute left-2.5 top-3 text-slate-400 pointer-events-none" />
                <input className={`${inputStyle} pl-8`} placeholder="customer@example.com"
                  value={form.emailId}
                  onChange={e => setForm({ ...form, emailId: e.target.value, _autoFilled: false })}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={() => { if (!form.TallySlNo) lookupCustomer("", form.emailId); }}
                  readOnly={isReadOnly("emailId")} />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Phone *</label>
              <div className="relative">
                <Phone size={13} className="absolute left-2.5 top-3 text-slate-400 pointer-events-none" />
                <input className={`${inputStyle} pl-8`} placeholder="10-digit mobile"
                  value={form.contact_no}
                  onChange={e => setForm({ ...form, contact_no: e.target.value })} />
              </div>
            </div>

            {/* Contact Person — editable + suggestions */}
            <div className="relative" ref={contactRef}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Contact Person</label>
              <div className="relative">
                <User size={13} className="absolute left-2.5 top-3 text-slate-400 pointer-events-none" />
                <input className={`${inputStyle} pl-8`} placeholder="Contact Person"
                  value={form.contact_person}
                  onChange={handleContactPersonChange}
                  onFocus={() => { if (contactSuggestions.length) setShowSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} />
              </div>
              {showSuggestions && (
                <SuggestionList suggestions={contactSuggestions} onSelect={handleSuggestionSelect} />
              )}
            </div>

            {/* Assign To */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Assign To</label>
              <SearchableDropdown options={employeeOptions} value={form.assigned_to}
                onChange={v => setForm({ ...form, assigned_to: v })} placeholder="Select Employee" />
            </div>

            {/* Support Type */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Support Type</label>
              <SearchableDropdown options={supportOptions} value={form.serviceType}
                onChange={v => setForm({ ...form, serviceType: v })} placeholder="Select Support Type" />
            </div>

            {/* Priority */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Priority</label>
              <SearchableDropdown options={priorityOptions} value={form.priority}
                onChange={v => setForm({ ...form, priority: v })} placeholder="Select Priority" />
            </div>

            {/* Created By (read-only) */}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Created By</label>
              <input className={`${inputStyle} bg-slate-100`} readOnly
                value={`${employeeName} (${employeeId})`} />
            </div>
          </div>

          {/* Description */}
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <AlignLeft size={12} /> Description
            </label>
            <textarea
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              rows={3} placeholder="Describe the issue…"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>

          <button onClick={handleSubmit} disabled={submitting}
            className="mt-5 w-full py-2.5 rounded-lg font-semibold text-white disabled:opacity-60 transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)" }}>
            {submitting ? "Submitting…" : "Submit & Send Email"}
          </button>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════
          REASSIGN MODAL
      ══════════════════════════════════════════════ */}
      {reassignModal && (
        <Modal onClose={() => setReassignModal(null)}>
          <h2 className="text-lg font-bold text-blue-900 mb-1 flex items-center gap-2">
            <ArrowRightLeft size={18} className="text-blue-600" /> Reassign Ticket
          </h2>
          <p className="text-xs text-slate-400 mb-5">
            #{reassignModal.ticket_id} — {reassignModal.customerName}
          </p>

          <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600">
            <span className="font-semibold">Current assignee:</span>{" "}
            {reassignModal.assigned_person || "—"}
          </div>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">
            Assign To *
          </label>
          <SearchableDropdown
            options={employeeOptions.filter(e => e.value !== reassignModal.assigned_to)}
            value={reassignTo}
            onChange={v => setReassignTo(v)}
            placeholder="Select new assignee"
          />

          <button onClick={handleReassign} disabled={submitting || !reassignTo}
            className="mt-5 w-full py-2.5 rounded-lg font-semibold text-white disabled:opacity-60 transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg,#1d4ed8,#3b82f6)" }}>
            {submitting ? "Reassigning…" : "Confirm Reassignment"}
          </button>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════
          CLOSE TICKET MODAL
      ══════════════════════════════════════════════ */}
      {closeModal && (
        <Modal onClose={() => setCloseModal(null)}>
          <h2 className="text-lg font-bold text-emerald-800 mb-1 flex items-center gap-2">
            <CheckCircle size={18} /> Close Ticket
          </h2>
          <p className="text-xs text-slate-400 mb-4">#{closeModal.ticket_id} — {closeModal.customerName}</p>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">
            Remarks / Resolution Notes *
          </label>
          <textarea
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
            rows={4} placeholder="Describe how the issue was resolved…"
            value={closeRemarks} onChange={e => setCloseRemarks(e.target.value)} />

          <p className="text-xs text-slate-400 mt-2 mb-4">
            📧 A resolution email with a 5-star feedback link will be sent to the customer.
          </p>

          <button onClick={handleClose} disabled={submitting || !closeRemarks.trim()}
            className="w-full py-2.5 rounded-lg font-semibold text-white disabled:opacity-60 transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}>
            {submitting ? "Closing…" : "Mark as Closed & Send Email"}
          </button>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════
          VIEW TICKET MODAL (with assignment timeline)
      ══════════════════════════════════════════════ */}
      {viewTicket && (() => {
        const segments = buildSegments(viewTicket);
        return (
          <Modal onClose={() => setViewTicket(null)} extraWide>
            <h2 className="text-lg font-bold text-indigo-900 mb-1 flex items-center gap-2">
              <Eye size={18} /> Ticket Details
            </h2>
            <p className="text-xs text-indigo-400 font-mono mb-5">{viewTicket.ticket_id}</p>

            {/* ── Basic info grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm mb-6">
              {[
                ["Tally SL No.",    viewTicket.TallySlNo || "—"],
                ["Customer Name",   viewTicket.customerName],
                ["Contact No.",     viewTicket.contact_no],
                ["Email ID",        viewTicket.emailId || "—"],
                ["Contact Person",  viewTicket.contact_person || "—"],
                ["Service Type",    viewTicket.serviceType || "—"],
                ["Assigned To",     viewTicket.assigned_person || "—"],
                ["Created By",      viewTicket.created_by_name || viewTicket.created_by],
                ["Generated Date",  fmtDate(viewTicket.generated_date)],
                ["Closed Date",     viewTicket.status === "closed" && viewTicket.closed_date
                                    ? fmtDate(viewTicket.closed_date) : "—"],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
                  <p className="font-medium text-slate-800 text-sm">{val}</p>
                </div>
              ))}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Priority</p>
                <PriorityBadge value={viewTicket.priority} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Status</p>
                <Badge status={viewTicket.status} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Client Feedback</p>
                <StarRating value={viewTicket.feedback} />
              </div>
            </div>

            {/* ── Assignment Duration Timeline ── */}
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 mb-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
                <Clock size={13} className="text-indigo-500" /> Assignment Duration Timeline
              </p>

              <div className="flex flex-col gap-2">
                {segments.map((seg, i) => {
                  const dur = segmentDuration(seg);
                  const endLabel = seg.to
                    ? fmtDate(seg.to)
                    : viewTicket.status === "closed" ? "—" : "Now (ongoing)";

                  return (
                    <div key={i}
                      className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border
                        ${seg.isActive
                          ? "bg-amber-50 border-amber-200"
                          : "bg-white border-slate-200"}`}>

                      {/* Ordinal dot */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
                        ${seg.isActive ? "bg-amber-500 text-white" : "bg-indigo-100 text-indigo-700"}`}>
                        {i + 1}
                      </div>

                      {/* Employee */}
                      <div className="flex items-center gap-1.5 min-w-[160px]">
                        <UserCheck size={13} className={seg.isActive ? "text-amber-600" : "text-slate-400"} />
                        <span className="text-sm font-semibold text-slate-700">{seg.name}</span>
                        {seg.isActive && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 ml-1">Active</span>
                        )}
                      </div>

                      {/* Time range */}
                      <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                        <span>{fmtDate(seg.from)}</span>
                        <span className="text-slate-300">→</span>
                        <span>{endLabel}</span>
                      </div>

                      {/* Duration chip */}
                      <div className="sm:ml-auto shrink-0">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold
                          ${seg.isActive
                            ? "bg-amber-100 text-amber-700"
                            : "bg-indigo-50 text-indigo-700"}`}>
                          <Clock size={11} />
                          {dur}
                          {seg.isActive && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse ml-0.5" />}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total duration */}
              <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500">Total Duration</span>
                <span className="text-sm font-bold text-indigo-700 flex items-center gap-1">
                  <Clock size={13} />
                  {fmtDuration(
                    viewTicket.generated_date,
                    viewTicket.status === "closed" ? viewTicket.closed_date : now.toISOString()
                  )}
                  {viewTicket.status !== "closed" &&
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse ml-1" />}
                </span>
              </div>
            </div>

            {/* Description */}
            {viewTicket.description && (
              <div className="border-t pt-3 mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Description</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewTicket.description}</p>
              </div>
            )}

            {/* Closing remarks */}
            {viewTicket.remarks && (
              <div className="border-t pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Closing Remarks</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewTicket.remarks}</p>
              </div>
            )}

            {/* Quick reassign from view modal (if not closed) */}
            {viewTicket.status !== "closed" && (
              <div className="border-t pt-4 mt-4 flex justify-end">
                <button
                  onClick={() => { setViewTicket(null); setReassignModal(viewTicket); setReassignTo(""); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors">
                  <ArrowRightLeft size={14} /> Reassign Ticket
                </button>
              </div>
            )}
          </Modal>
        );
      })()}
    </>
  );
}